<template>
  <div class="bar-codes-page">
    <block-buttons
      title="Ценники"
      description="Выберите товары и подготовьте ценники к печати"
      :actions="toolbarActions"
    >
      <template #icon>
        <Barcode :size="22" />
      </template>
    </block-buttons>

    <section
      class="bar-codes-page__card"
      aria-label="Настройки ценников"
    >
      <div class="bar-codes-page__fields">
        <label>Категория товаров

          <select
            v-model="selectedCategoryId"
            :disabled="loadingCategories"
          >
            <option value="-1">Выберите категорию</option>

            <option
              v-for="({ title, id }) in categories"
              :key="id"
              :value="id"
            >
              {{ title }}
            </option>
          </select>
        </label>

        <label>Формат ценника
          <select v-model="isPrintA4">
            <option :value="false">Этикетка 4 × 2 см · со штрихкодом</option>
            <option :value="true">А4 · ценник 6 × 4 см</option>
          </select>
        </label>

        <label>Режим печати
          <select
            v-model="isPrintOnlyOne"
            @change="resetSelection"
          >
            <option :value="false">Несколько товаров · по остатку</option>
            <option :value="true">Один товар · указать количество</option>
          </select>
        </label>

        <label v-if="isPrintOnlyOne">
          Количество ценников
          <input
            v-model="printCount"
            type="number"
            min="1"
            max="1000"
            step="1"
            :aria-invalid="!validPrintCount"
          >
        </label>
      </div>

      <p
        v-if="isPrintOnlyOne && !validPrintCount"
        class="bar-codes-page__error"
        role="alert"
      >
        Введите целое количество от 1 до 1000.
      </p>

      <p
        v-else
        class="bar-codes-page__hint"
      >
        {{ isPrintOnlyOne ? 'Выберите один товар в списке ниже.' : 'Для каждого товара печатается ценник на каждую единицу остатка. Если остаток нулевой или отрицательный — один ценник.' }}
      </p>
    </section>

    <section
      class="bar-codes-page__card"
      aria-label="Выбор товаров"
      :aria-busy="loadingProducts"
    >
      <div class="bar-codes-page__section-heading">
        <h3>
          Товары
          <span>{{ selectedProducts.length }} выбрано</span>
        </h3>

        <input
          v-model="search"
          type="search"
          placeholder="Поиск по названию или штрихкоду"
          aria-label="Поиск товаров"
        >
      </div>

      <div
        v-if="products.length && !isPrintOnlyOne"
        class="bar-codes-page__selection"
      >
        <label>
          <input
            type="checkbox"
            :checked="allSelected"
            @change="selectAll($event.target.checked)"
          >
          Все товары категории ({{ products.length }})
        </label>

        <button
          type="button"
          :disabled="!selectedIds.length"
          @click="resetSelection"
        >
          Снять выбор
        </button>
      </div>

      <p
        v-if="loadingCategories || loadingProducts"
        class="bar-codes-page__empty"
        role="status"
      >
        Загрузка…
      </p>

      <p
        v-else-if="!products.length"
        class="bar-codes-page__empty"
      >
        {{ selectedCategoryId == null ? 'Выберите категорию, чтобы загрузить товары.' : 'В этой категории нет товаров.' }}
      </p>

      <p
        v-else-if="!filteredProducts.length"
        class="bar-codes-page__empty"
      >
        Товары не найдены. Измените поисковый запрос.
      </p>

      <div
        v-else
        class="bar-codes-page__products"
      >
        <label
          v-for="product in filteredProducts"
          :key="product.id"
          class="bar-codes-page__product"
        >
          <input
            :type="isPrintOnlyOne ? 'radio' : 'checkbox'"
            name="label-product"
            :checked="selectedIds.includes(product.id)"
            @change="selectProduct(product.id, $event.target.checked)"
          >

          <span class="bar-codes-page__product-name">
            <strong>{{ product.title }}</strong>
            <small>Штрихкод: {{ product.code || '—' }} · Остаток: {{ product.count || 0 }}</small>
          </span>

          <span class="bar-codes-page__price">
            {{ productPrice(product) }} руб.

            <small v-if="Number(product.salePrice)">Цена со скидкой</small>
          </span>
        </label>
      </div>
    </section>

    <section
      class="bar-codes-page__card"
      aria-label="Предпросмотр ценников"
    >
      <div class="bar-codes-page__section-heading">
        <h3>Предпросмотр</h3>
        <span>Ценников: {{ labelCount }}</span>
      </div>

      <p
        v-if="labelCount > maxLabels"
        class="bar-codes-page__error"
        role="alert"
      >
        За один раз можно напечатать до {{ maxLabels }} ценников. Уменьшите количество или выберите меньше товаров.
      </p>

      <p
        v-else-if="!labels.length"
        class="bar-codes-page__empty"
      >
        Выберите товары и количество для предпросмотра.
      </p>

      <div
        v-else
        class="bar-codes-page__preview"
      >
        <div
          ref="printLabels"
          class="price-labels"
        >
          <label-styles />

          <div
            v-for="label in labels"
            :key="label.key"
            :class="isPrintA4 ? 'tag-a4' : 'tag'"
          >
            <div class="owner">{{ organizationName }}</div>
            <div class="name">{{ label.product.title }}</div>
            <div class="price">
              {{ productPrice(label.product) }}
              <span>руб.</span>
            </div>

            <div
              v-if="!isPrintA4"
              class="barcode-wrapper"
            >
              <vue-barcode
                :value="label.product.code"
                :height="20"
                :display-value="false"
                flat
                font-options="italic"
                :margin="0"
                :width="2"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { Barcode, Printer } from 'lucide-vue'
import VueBarcode from 'vue-barcode'
import BlockButtons from '@/components/blockButtons'
import labelStyles from './labelStyles'

export default {
  name: 'BarCodesV2',
  components: {
    Barcode,
    VueBarcode,
    BlockButtons,
    LabelStyles: {
      functional: true,
      render: h => h('style', {
        domProps: {
          textContent: labelStyles
        }
      })
    }
  },
  data () {
    return {
      selectedCategoryId: -1,
      selectedIds: [],
      search: '',
      isPrintOnlyOne: false,
      isPrintA4: false,
      printCount: 1,
      loadingCategories: false,
      loadingProducts: false,
      error: '',
      maxLabels: 1000,
      organizationName: 'ИП Амириллаева С.М.'
    }
  },
  computed: {
    ...mapGetters('category', { categories: 'getData' }),
    ...mapGetters('product', { products: 'getData' }),
    toolbarActions () {
      return [
        {
          id: 'print',
          label: 'Печать',
          icon: Printer,
          primary: true,
          disabled: !this.labels.length,
          handler: this.print
        }
      ]
    },
    validPrintCount () {
      const count = Number(this.printCount)

      return Number.isInteger(count) && count > 0 && count <= this.maxLabels
    },
    filteredProducts () {
      const query = this.search.trim().toLocaleLowerCase('ru')

      return this.products.filter(({ title, code }) => `${title} ${code || ''}`.toLocaleLowerCase('ru').includes(query))
    },
    selectedProducts () {
      return this.products.filter(({ id }) => this.selectedIds.includes(id))
    },
    allSelected () {
      return this.products.length > 0 && this.selectedProducts.length === this.products.length
    },
    labelCount () {
      return this.selectedProducts.reduce((total, product) => total + this.quantity(product), 0)
    },
    labels () {
      if (this.labelCount > this.maxLabels) {
        return []
      }

      return this.selectedProducts.flatMap(product => Array.from({
        length: this.quantity(product)
      }, (_, i) => ({ key: `${product.id}-${i}`, product })))
    }
  },
  async mounted () {
    await this.loadDataWrapper()
  },
  methods: {
    ...mapActions('category', ['loadData']),
    ...mapActions('product', ['getDataByCategoryId']),
    async loadDataWrapper () {
      if (this.loading) {
        return
      }

      this.$bus.emit('start-loader')
      this.loading = true

      await this.loadData()

      this.loading = false
      this.$bus.emit('stop-loader')
    },
    productPrice ({ salePrice = 0, price = 0 } = {}) {
      return Number(salePrice) || Number(price)
    },
    quantity (product) {
      if (this.isPrintOnlyOne) {
        return this.validPrintCount ? Number(this.printCount) : 0
      }

      const count = Number(product.count)

      return Number.isFinite(count) ? Math.max(1, Math.floor(count)) : 1
    },
    resetSelection () {
      this.selectedIds = []
    },
    selectProduct (id, checked) {
      this.selectedIds = this.isPrintOnlyOne ? [id] : checked ? [...this.selectedIds, id] : this.selectedIds.filter(value => value !== id)
    },
    selectAll (checked) { this.selectedIds = checked ? this.products.map(product => product.id) : [] },
    async print () {
      if (!this.labels.length || this.loadingProducts) {
        return
      }

      const printWindow = window.open('', '_blank', 'height=600,width=1000')

      if (!printWindow) {
        this.$notify({
          type: 'error',
          title: 'Разрешите всплывающие окна для печати ценников',
          duration: 3000
        })

        return
      }
      await this.$nextTick()

      const pageStyles = this.isPrintA4
        ? '@page { size: A4; margin: 10mm; }'
        : '@page { size: 4cm 2cm; margin: 0; } .price-labels .tag { break-after: page; }'
      printWindow.document.open()
      printWindow.document.write(`<!doctype html><html lang="ru"><head><meta charset="utf-8"><title>Печать ценников</title><style>body { margin: 0; } ${pageStyles}</style></head><body></body></html>`)
      printWindow.document.body.appendChild(this.$refs.printLabels.cloneNode(true))
      printWindow.addEventListener('afterprint', () => printWindow.close(), { once: true })
      printWindow.addEventListener('load', () => {
        printWindow.focus()
        printWindow.print()
      }, { once: true })
      printWindow.document.close()
    }
  }
}
</script>

<style scoped lang="scss" src="./style.scss" />
